import React, { useState } from 'react';
import {
  Box,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  CircularProgress,
  Typography,
} from '@mui/material';
import {
  Business as BusinessIcon,
  Build as BuildIcon,
} from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';
import { enqueueSnackbar } from 'notistack';

/**
 * RoleSwitcher Component
 * 
 * A toggle component for dual-role users to switch between customer and trade company modes.
 * - Only visible for users with account_mode='dual'
 * - Persists role selection to database
 * - Clears React Query cache on role switch to prevent stale data
 * - Updates UI immediately with optimistic update
 */
export const RoleSwitcher: React.FC = () => {
  const { user, switchRole, isDualRole } = useAuth();
  const [isSwitching, setIsSwitching] = useState(false);

  // Only show for dual-role users
  if (!isDualRole() || !user) {
    return null;
  }

  const handleRoleChange = async (
    _event: React.MouseEvent<HTMLElement>,
    newRole: 'customer' | 'trade_company' | null,
  ) => {
    // Don't allow deselecting (null means user clicked the same button twice)
    if (!newRole || newRole === user.active_role) {
      return;
    }

    setIsSwitching(true);

    try {
      // Switch role in auth context (updates database, local state, and invalidates queries)
      await switchRole(newRole);

      // Show success message
      const roleLabel = newRole === 'customer' ? 'Client' : 'Trade Business';
      enqueueSnackbar(`Switched to ${roleLabel} mode`, {
        variant: 'success',
        autoHideDuration: 2000,
      });
    } catch (error) {
      console.error('Error switching role:', error);
      enqueueSnackbar('Failed to switch role. Please try again.', {
        variant: 'error',
      });
    } finally {
      setIsSwitching(false);
    }
  };

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 1,
        position: 'relative',
      }}
    >
      <Typography
        variant="caption"
        sx={{
          color: 'rgba(238,242,232,0.7)',
          fontWeight: 500,
          display: { xs: 'none', sm: 'block' },
        }}
      >
        View as:
      </Typography>

      <ToggleButtonGroup
        value={user.active_role}
        exclusive
        onChange={handleRoleChange}
        aria-label="role switcher"
        size="small"
        disabled={isSwitching}
        sx={{
          '& .MuiToggleButton-root': {
            px: { xs: 1, sm: 2 },
            py: 0.5,
            fontSize: { xs: '0.75rem', sm: '0.875rem' },
            fontWeight: 500,
            textTransform: 'none',
            border: '1px solid',
            borderColor: 'rgba(238,242,232,0.28)',
            color: 'rgba(238,242,232,0.85)',
            '&.Mui-selected': {
              backgroundColor: 'primary.main',
              color: 'primary.contrastText',
              '&:hover': {
                backgroundColor: 'primary.dark',
              },
            },
            '&:hover': {
              backgroundColor: 'rgba(238,242,232,0.08)',
            },
          },
        }}
      >
        <ToggleButton value="customer" aria-label="customer mode">
          <Tooltip title="Client - Post jobs and manage projects">
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <BusinessIcon sx={{ fontSize: { xs: 16, sm: 18 } }} />
              <Box component="span" sx={{ display: { xs: 'none', sm: 'inline' } }}>Client</Box>
            </Box>
          </Tooltip>
        </ToggleButton>

        <ToggleButton value="trade_company" aria-label="trade company mode">
          <Tooltip title="Trade Business - Browse jobs and submit quotes">
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <BuildIcon sx={{ fontSize: { xs: 16, sm: 18 } }} />
              <Box component="span" sx={{ display: { xs: 'none', sm: 'inline' } }}>Trade</Box>
            </Box>
          </Tooltip>
        </ToggleButton>
      </ToggleButtonGroup>

      {isSwitching && (
        <CircularProgress
          size={20}
          sx={{
            position: 'absolute',
            right: -30,
            top: '50%',
            transform: 'translateY(-50%)',
          }}
        />
      )}
    </Box>
  );
};
